import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useFormContext } from 'react-hook-form'
import PhoneInput from 'react-phone-number-input'
import clsx from 'clsx'
import RadioGroup from '@components/RadioGroup/RadioGroup'
import Input from '@components/Input'
import FormError from '@components/FormError'
import RadioButton from '@components/RadioButton'
import PatientNameWrapper from '../PatientNameWrapper'
import useStyles from './ContactDetails.styles'
import Birthdate from '../Birthdate/Birthdate'
import { isSafari } from 'src/helpers/detectSafari'
import { ReactComponent as DefaultPractitionerImage } from 'src/images/practitioner-default.svg'
import { ReactComponent as EnvelopeImage } from 'src/images/envelope.svg'
import { useSurveyContext } from '@context/SurveyContext'

interface Props {
  isBookingForAnotherPerson: boolean
}

const ContactDetails = ({ isBookingForAnotherPerson }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { setPhoneNumber } = useSurveyContext()
  const {
    register,
    control,
    formState: { errors },
    watch,
  } = useFormContext()

  const [focus, setFocus] = useState(false)

  const onPhoneBlurCallback = () => {
    setFocus(false)
  }

  useEffect(() => {
    const subscription = watch((value) => setPhoneNumber(value.phone))

    return () => subscription.unsubscribe()
  }, [watch, setPhoneNumber])

  return (
    <div className={classes.root}>
      <RadioGroup>
        <RadioButton id="female" label={t('mrs')} {...register('gender')} />
        <RadioButton id="male" label={t('mr')} {...register('gender')} />
        <RadioButton id="diverse" label={t('divers')} {...register('gender')} />
      </RadioGroup>
      <PatientNameWrapper>
        <Input
          required
          icon={<DefaultPractitionerImage />}
          placeholder={t('first_name')}
          type="text"
          data-test="firstName"
          {...register('firstName')}
          info={errors.firstName && <FormError message={errors.firstName.message} />}
        />
        <Input
          required
          icon={<DefaultPractitionerImage />}
          placeholder={t('last_name')}
          type="text"
          data-test="lastName"
          {...register('lastName')}
          info={errors.lastName && <FormError message={errors.lastName.message} />}
        />
      </PatientNameWrapper>
      {!isBookingForAnotherPerson && <Birthdate id="birthdate" />}
      <div className={clsx(classes.phoneNumberInputRoot, focus && classes.phoneNumberInputFocus)}>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <PhoneInput
              international
              inputComponent={Input}
              defaultCountry="DE"
              country="DE"
              placeholder={t('phone')}
              data-test="phone"
              autoComplete={isSafari ? 'off' : 'tel'}
              // putting search phrase into name of this input disables
              // safari autofill contact functionality which is broken
              name={isSafari ? 'phoneSearch' : 'phone'}
              value={field.value}
              onFocus={() => setFocus(true)}
              onChange={(value) => {
                field.onChange({ target: { name: 'phone', value } })
              }}
              onBlur={() => {
                field.onBlur()
                onPhoneBlurCallback()
              }}
              ref={field.ref}
              required
            />
          )}
        />

        <div className={classes.phoneNumberInfo}>
          {errors.phone ? <FormError message={errors.phone.message} /> : t('phone_info')}
        </div>
      </div>
      <Input
        required
        icon={<EnvelopeImage />}
        type="email"
        data-test="email"
        placeholder={t('email')}
        {...register('email')}
        info={errors.email ? <FormError message={errors.email.message} /> : t('email_info')}
      />
    </div>
  )
}

export default ContactDetails
